import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { IoCodeSlashOutline, IoCreateOutline } from "react-icons/io5";
import { useNavigate } from "react-router";

import ReadParameterGroup from "./ReadParameterGroup.graphql";
import UpsertParameterGroup from "./UpsertParameterGroup.graphql";
import useNotifications, { notifyMutationErrors } from "../../../../../components/Notifications";
import * as UI from "../../../../../modules/ui-kit";
import CodeEditor from "../../../components/CodeEditor";
import { StyledEditorLayout } from "../../../components/StyledEditorLayout";
import { sameSource } from "../../../modules/compare";
import { useUnsavedChangesEditor } from "../../../modules/unsaved-changes";
import { ACCESS_TIER_OPTIONS } from "../../accessTiers";

function jsonApolloReplacer(key, value) {
    if (key === "__typename") return undefined;
    if (value === null) return undefined;
    return value;
}

export default function ParameterGroupEditor(props) {
    const { guard, id } = props;
    const navigate = useNavigate();

    const notifications = useNotifications();

    const [mode, setMode] = useState<"form" | "json">("form");

    const readParameterGroup = useQuery(
        gql(ReadParameterGroup),
        {
            variables: {
                id,
            },
        },
    );

    // ParameterGroupDefinition rejects unknown keys, and tier is a mutation
    // argument rather than part of the definition.
    const { tier: _storedTier, ...storedSource } = readParameterGroup.data?.parameterGroup ?? {};

    const [currentSource, setCurrentSource] = useState(undefined);
    const [jsonText, setJsonText] = useState("");
    const [jsonError, setJsonError] = useState(undefined);
    const [saving, setSaving] = useState(false);
    const [tier, setTier] = useState(undefined);



    useEffect(
        () => {
            setCurrentSource(undefined);
            setJsonText(JSON.stringify(storedSource, jsonApolloReplacer, 2));
            setJsonError(undefined);
            setTier(undefined);
        },
        [id, readParameterGroup.data],
    );

    const activeSource = currentSource ?? storedSource;

    // tier lives outside the definition - it is a mutation argument
    const storedTier = readParameterGroup.data?.parameterGroup?.tier ?? undefined;
    const activeTier = tier ?? storedTier;
    const tierDirty = tier !== undefined && tier !== storedTier;

    const dirty = !sameSource(activeSource, storedSource) || tierDirty;

    const [upsertParameterGroup] = useMutation(
        gql(UpsertParameterGroup),
        {
            awaitRefetchQueries: true,
            refetchQueries: [
                gql(ReadParameterGroup),
                "ListParameterGroups",
            ],
            variables: {
                id,
            },
        },
    );

    function updateField(key: string, value: unknown) {
        setCurrentSource({
            ...activeSource,
            [key]: value,
        });
    }

    function onJsonChange(value: string | undefined) {
        const text = value ?? "";
        setJsonText(text);
        try {
            const parsed = JSON.parse(text);
            setCurrentSource(parsed);
            setJsonError(undefined);
        } catch (e) {
            setJsonError((e as Error).message);
        }
    }

    function switchMode(targetMode: "form" | "json") {
        setJsonError(undefined);
        if (targetMode === "json") {
            setJsonText(JSON.stringify(activeSource, jsonApolloReplacer, 2));
        }
        setMode(targetMode);
    }

    async function save() {
        if (jsonError && mode === "json") return false;

        setSaving(true);

        try {
            const payload = JSON.parse(JSON.stringify({
                ...activeSource,
                name: activeSource.name ?? [],
                order: activeSource.order !== undefined && activeSource.order !== null ? Number(activeSource.order) : 0,
            }, jsonApolloReplacer));

            const result = await upsertParameterGroup({
                variables: {
                    definition: payload,
                    // omitted unless changed, so the stored tier is preserved
                    ...(tierDirty && { tier: activeTier }),
                },
            });

            if (result.data?.upsertParameterGroup?.errors?.length) {
                notifyMutationErrors(notifications, result.data.upsertParameterGroup.errors);
                return false;
            }

            setCurrentSource(undefined);
            setTier(undefined);
            notifications.success("Saved.");

            const newID = result.data?.upsertParameterGroup?.parameterGroup?.id;
            if (newID && newID !== id) {
                navigate(`../${ newID }`);
            }

            return true;
        } finally {
            setSaving(false);
        }
    }

    useUnsavedChangesEditor(guard, save, dirty);

    if (readParameterGroup.loading) return "loading";
    if (readParameterGroup.error) return "error";

    function onSubmit(event) {
        event.preventDefault();
        void save();
    }

    function onReset() {
        setCurrentSource(undefined);
        setJsonText(JSON.stringify(storedSource, jsonApolloReplacer, 2));
        setJsonError(undefined);
        setTier(undefined);
    }


    return (
        <StyledEditorLayout>
            <div className="mode-tabs">
                <button
                    className={ mode === "form" ? "active" : "" }
                    onClick={ () => switchMode("form") }
                    type="button"
                >
                    <IoCreateOutline /> Form View
                </button>
                <button
                    className={ mode === "json" ? "active" : "" }
                    onClick={ () => switchMode("json") }
                    type="button"
                >
                    <IoCodeSlashOutline /> JSON Source
                </button>
            </div>

            <UI.Form
                className="editor-body"
                onReset={ onReset }
                onSubmit={ onSubmit }
            >
                { mode === "form" ? (
                    <>
                        <UI.Field>
                            <UI.FieldLabel>name</UI.FieldLabel>
                            <UI.FieldDescription>Multi-language translations for the parameter group name</UI.FieldDescription>
                            <UI.TranslatedInput
                                disabled={ saving }
                                label="name"
                                onChange={ (val) => updateField("name", val) }
                                value={ activeSource.name ?? [] }
                            />
                        </UI.Field>

                        <UI.Field>
                            <UI.FieldLabel>tier</UI.FieldLabel>
                            <UI.Select
                                isDisabled={ saving }
                                onSelect={ setTier }
                                options={ ACCESS_TIER_OPTIONS }
                                value={ activeTier }
                            />
                        </UI.Field>

                        <UI.Field>
                            <UI.FieldLabel>order</UI.FieldLabel>
                            <UI.FieldDescription>Sort order index for ordering parameter groups</UI.FieldDescription>
                            <UI.TextInput
                                disabled={ saving }
                                onChange={ (val) => updateField("order", val === "" ? undefined : Number(val)) }
                                placeholder="0"
                                type="number"
                                value={ activeSource.order !== undefined && activeSource.order !== null ? String(activeSource.order) : "" }
                            />
                        </UI.Field>
                    </>
                ) : (
                    <UI.Field className="json-field">
                        <UI.FieldLabel>Raw Parameter Group Definition</UI.FieldLabel>
                        <UI.FieldDescription>Edit raw parameter group JSON definition (name, order)</UI.FieldDescription>
                        <div className="json-editor-wrapper">
                            <CodeEditor
                                key={ `${ id }-${ mode }` }
                                onChange={ onJsonChange }
                                value={ jsonText }
                            />
                        </div>
                        { jsonError && <span className="errors">Invalid JSON: { jsonError }</span> }
                    </UI.Field>
                ) }


                <UI.ButtonGroup>
                    <UI.Button
                        disabled={ !dirty || saving }
                        type="reset"
                    >
                        Reset
                    </UI.Button>
                    <UI.Button
                        $appearance="primary"
                        disabled={ !dirty || (mode === "json" && !!jsonError) || saving }
                        type="submit"
                    >
                        { saving ? "Saving…" : "Save" }
                    </UI.Button>
                </UI.ButtonGroup>
            </UI.Form>
        </StyledEditorLayout>
    );
}
